// App.js
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  Instagram,
  Facebook,
  Linkedin,
  ArrowRight,
  Sparkles,
  Loader2,
} from "lucide-react";

// ... (Particle, useCountdown, etc., as before. No changes needed there)

const socials = [
  { name: "Email", icon: <Mail size={22} />, url: "mailto:hello@elevate.com" },
  { name: "Instagram", icon: <Instagram size={22} />, url: "https://instagram.com/" },
  { name: "Facebook", icon: <Facebook size={22} />, url: "https://facebook.com/" },
  { name: "LinkedIn", icon: <Linkedin size={22} />, url: "https://linkedin.com/" },
];

const LAUNCH_DATE = new Date("2025-09-01T00:00:00");

const PARTICLE_COUNT = 36;
function randomBetween(a, b) {
  return Math.random() * (b - a) + a;
}
function Particle({ w, h }) {
  const [pos, setPos] = useState({
    x: randomBetween(0, w),
    y: randomBetween(0, h),
    r: randomBetween(0.8, 1.6),
    angle: randomBetween(0, 360),
    speed: randomBetween(0.12, 0.36),
    phase: Math.random() * 2 * Math.PI,
    color: ["#bafbe7", "#8cd9fc", "#c4b5fd", "#a7f3d0"][Math.floor(Math.random() * 4)],
    size: randomBetween(4, 9),
    id: Math.random().toString(36),
  });

  useEffect(() => {
    let frame;
    const animate = () => {
      setPos((prev) => {
        let a = prev.angle + prev.speed;
        let y = prev.y + Math.sin(a / 23 + prev.phase) * 0.9;
        let x = prev.x + Math.cos(a / 28 + prev.phase) * 0.8;
        x = (x + w) % w;
        y = (y + h) % h;
        return { ...prev, x, y, angle: a };
      });
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [w, h]);

  return (
    <motion.div
      style={{
        position: "absolute",
        left: pos.x,
        top: pos.y,
        width: pos.size,
        height: pos.size,
        borderRadius: "50%",
        background: pos.color,
        opacity: 0.22,
        filter: "blur(0.7px)",
        zIndex: 0,
        pointerEvents: "none",
      }}
      whileHover={{ opacity: 0.6, scale: 1.2 }}
      transition={{ type: "spring", stiffness: 240, damping: 18 }}
    />
  );
}
function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);
  let left = Math.max(0, target - now);
  let days = Math.floor(left / (1000 * 60 * 60 * 24));
  let hours = Math.floor((left / (1000 * 60 * 60)) % 24);
  let minutes = Math.floor((left / (1000 * 60)) % 60);
  let seconds = Math.floor((left / 1000) % 60);
  return { days, hours, minutes, seconds, left };
}

export default function App() {
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [dim, setDim] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => {
    const onResize = () => setDim({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const { days, hours, minutes, seconds } = useCountdown(LAUNCH_DATE);

  function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setSubmitted(true);
      setLoading(false);
    }, 1200);
  }

  return (
    <>
      <style>
        {`
        html, body, #root {
          height: 100%;
          margin: 0;
          padding: 0;
          overflow: hidden;
        }
        body {
          background: #151d41;
        }
        .soon-card {
          background: rgba(255,255,255,0.13);
          border-radius: 2.2rem;
          border: 1.6px solid rgba(255,255,255,0.21);
          box-shadow: 0 8px 48px 8px #031d1a29, 0 2px 4px #172b4212;
          padding: 2.8rem 2.2rem 2.2rem 2.2rem;
          max-width: 410px;
          width: 98vw;
          margin: 0 auto;
          position: relative;
          z-index: 5;
          display: flex;
          flex-direction: column;
          align-items: center;
          transition:
          box-shadow .25s, 
          transform .25s,
          width .33s cubic-bezier(.42,0,.58,1),
          max-width .33s cubic-bezier(.42,0,.58,1),
          min-width .33s cubic-bezier(.42,0,.58,1),
          height .33s cubic-bezier(.42,0,.58,1),
          max-height .33s cubic-bezier(.42,0,.58,1),
          min-height .33s cubic-bezier(.42,0,.58,1),
          padding .33s cubic-bezier(.42,0,.58,1);
        }
        .soon-title {
          font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
          font-weight: 900;
          font-size: 2.5rem;
          color: #fff;
          letter-spacing: -1px;
          text-align: center;
        }
        .soon-desc {
          margin-top: 1.2rem;
          color: #e0e7ef;
          font-size: 1.22rem;
          text-align: center;
          font-weight: 500;
          line-height: 1.6;
          letter-spacing: 0.02em;
        }
        .soon-small {
          color: #b2c0dd;
          margin-top: .2rem;
          margin-bottom: .7rem;
          font-size: 0.97rem;
          text-align: center;
        }
        .soon-btn, .soon-form-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.55em;
          font-weight: 700;
          font-size: 1.07rem;
          padding: 0.85em 1.65em;
          border: none;
          border-radius: 1.3em;
          background: linear-gradient(90deg,#6d7dfc 30%,#3beea7 90%);
          color: #fff;
          cursor: pointer;
          box-shadow: 0 2px 16px 0 #226d4c32;
          transition: transform 0.14s, box-shadow 0.18s;
        }
        .soon-btn:hover, .soon-form-btn:hover {
          transform: scale(1.05);
          box-shadow: 0 8px 22px 0 #09b67221;
        }
        .soon-cancel {
          margin-top: 0.6em;
          background: none;
          border: none;
          color: #a7bde2;
          text-decoration: underline;
          font-size: 0.97em;
          cursor: pointer;
          transition: color .2s;
        }
        .soon-cancel:hover {
          color: #29edb6;
        }
        .soon-form input[type="email"] {
          width: 100%;
          padding: 0.7em 1em;
          border-radius: 0.8em;
          border: 1.1px solid #d5f6ed2e;
          background: rgba(255,255,255,0.07);
          color: #fff;
          font-size: 1rem;
          margin-bottom: 0.8em;
          font-family: inherit;
          outline: none;
          transition: border .2s;
        }
        .soon-form input[type="email"]:focus {
          border: 1.5px solid #5eead4;
        }
        .soon-socials {
          margin-top: 2.2em;
          display: flex;
          gap: 1.1em;
        }
        .soon-social-link {
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.13);
          color: #e0f2fe;
          border-radius: 50%;
          width: 2.6em;
          height: 2.6em;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.05em;
          transition: background .19s, color .18s, transform .17s;
          box-shadow: 0 1px 8px #18be8e21;
        }
        .soon-social-link:hover {
          background: linear-gradient(100deg, #a5b4fc 0%, #4ade80 100%);
          color: #164e63;
          transform: scale(1.18) rotate(8deg);
        }
        .soon-thank {
          color: #5eead4;
          font-size: 1.22rem;
          font-weight: 500;
          margin-bottom: 0.7em;
          text-align: center;
        }
        .soon-thank-msg {
          color: #fff;
          text-align: center;
          font-weight: 600;
          font-size: 1.18em;
        }
        .soon-footer {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 24px;
          text-align: center;
          color: #e0e7ef42;
          font-size: 0.92em;
          font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
          letter-spacing: .22em;
          z-index: 9;
          user-select: none;
        }
        .countdown-row {
          display: flex;
          gap: 1.2em;
          align-items: center;
          justify-content: center;
          margin: 2em 0 1em 0;
        }
        .countdown-cell {
          text-align: center;
        }
        .countdown-val {
          font-size: 1.6em;
          font-weight: bold;
          color: #4ade80;
          text-shadow: 0 3px 10px #151d41bb;
        }
        .countdown-lbl {
          font-size: .82em;
          color: #b2c0dd;
          font-weight: 500;
        }
        @media (max-width: 600px) {
          .soon-card {
            padding: 2.1rem 0.8rem 1.3rem 0.8rem;
          }
          .soon-title {
            font-size: 2rem;
          }
        }
        `}
      </style>

      <div
        style={{
          height: "100vh",
          width: "100vw",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* ... Animated backgrounds and card exactly as before ... */}

        {/* 1. Animated Gradient BG */}
        <motion.div
          style={{
            position: "absolute",
            inset: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 0,
            pointerEvents: "none",
            background:
              "linear-gradient(120deg, #1e2151 0%, #124562 50%, #105650 100%)",
            transition: "background 1s",
          }}
          animate={{
            background: [
              "linear-gradient(120deg, #1e2151 0%, #124562 50%, #105650 100%)",
              "linear-gradient(110deg, #1e2151 0%, #2e4a7b 60%, #107a68 100%)",
              "linear-gradient(130deg, #16214e 0%, #205969 55%, #16a87f 100%)",
              "linear-gradient(120deg, #1e2151 0%, #124562 50%, #105650 100%)",
            ],
          }}
          transition={{
            duration: 18,
            ease: "linear",
            repeat: Infinity,
          }}
        />

        {/* 2. Animated Blobs */}
        <motion.div
          style={{
            position: "absolute",
            top: "10%",
            left: "13%",
            width: 320,
            height: 320,
            background:
              "linear-gradient(120deg, #6d7dfc77, #3beea799 60%, #93c5fd44)",
            filter: "blur(80px)",
            borderRadius: "44% 56% 60% 40% / 54% 51% 49% 46%",
            zIndex: 1,
            pointerEvents: "none",
          }}
          animate={{
            scale: [1, 1.12, 0.95, 1],
            borderRadius: [
              "44% 56% 60% 40% / 54% 51% 49% 46%",
              "50% 60% 60% 50% / 60% 60% 40% 40%",
              "39% 61% 56% 44% / 58% 53% 47% 42%",
              "44% 56% 60% 40% / 54% 51% 49% 46%",
            ],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          style={{
            position: "absolute",
            top: "68%",
            left: "72%",
            width: 200,
            height: 200,
            background:
              "linear-gradient(120deg, #c4b5fd77, #29edb677 65%, #e0e7ef55)",
            filter: "blur(50px)",
            borderRadius: "40% 60% 55% 45% / 52% 48% 52% 48%",
            zIndex: 1,
            pointerEvents: "none",
          }}
          animate={{
            scale: [1, 1.13, 0.97, 1],
            borderRadius: [
              "40% 60% 55% 45% / 52% 48% 52% 48%",
              "58% 42% 55% 45% / 52% 60% 40% 48%",
              "40% 60% 55% 45% / 52% 48% 52% 48%",
            ],
          }}
          transition={{
            duration: 14,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* 3. Animated Interactive Particles */}
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          {Array.from({ length: PARTICLE_COUNT }).map((_, i) => (
            <Particle key={i} w={dim.w} h={dim.h} />
          ))}
        </div>

        {/* ...Card and footer as before... */}

        <motion.div
          className="soon-card"
          initial={{ y: 38, opacity: 0, scale: 0.97 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          transition={{ duration: 0.55, ease: "anticipate" }}
          style={{ zIndex: 5, position: "relative" }}
        >
          <motion.div
            initial={{ rotate: -10, scale: 0.85, opacity: 0 }}
            animate={{ rotate: 0, scale: 1, opacity: 1 }}
            transition={{ delay: 0.18, type: "spring", stiffness: 145 }}
            style={{ marginBottom: "1em" }}
          >
            <Sparkles color="#5eead4" size={40} style={{ filter: "drop-shadow(0 4px 12px #65ffa522)" }}/>
          </motion.div>
          <motion.h1
            className="soon-title"
            initial={{ opacity: 0, y: -18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.22 }}
          >
            Elevate Digital Global
          </motion.h1>
          <motion.div
            className="soon-desc"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.29 }}
          >
            <span style={{ color: "#4ade80", fontWeight: 700, letterSpacing: "-0.02em" }}>
              Something new
            </span>{" "}
            is coming soon.
          </motion.div>
          <motion.div
            className="soon-small"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.36 }}
          >
            Stay ahead — get notified when we launch.
          </motion.div>
          <div className="countdown-row">
            <div className="countdown-cell">
              <div className="countdown-val">{days}</div>
              <div className="countdown-lbl">days</div>
            </div>
            <div className="countdown-cell">
              <div className="countdown-val">{hours}</div>
              <div className="countdown-lbl">hrs</div>
            </div>
            <div className="countdown-cell">
              <div className="countdown-val">{minutes}</div>
              <div className="countdown-lbl">min</div>
            </div>
            <div className="countdown-cell">
              <div className="countdown-val">{seconds}</div>
              <div className="countdown-lbl">sec</div>
            </div>
          </div>
          <AnimatePresence>
            {!showForm && !submitted && (
              <motion.button
                className="soon-btn"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setShowForm(true)}
              >
                <Mail size={20} />
                Notify Me
                <ArrowRight size={17} style={{ marginLeft: 2, marginTop: 2 }}/>
              </motion.button>
            )}
          </AnimatePresence>
          <AnimatePresence>
            {showForm && !submitted && (
              <motion.form
                key="form"
                className="soon-form"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                style={{
                  marginTop: "1.6em",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
                onSubmit={handleSubmit}
                autoComplete="off"
              >
                <input
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Your email address"
                  disabled={loading}
                  autoFocus
                />
                <button
                  type="submit"
                  className="soon-form-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="spin" style={{ marginRight: 6, verticalAlign: -4 }} />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Mail size={18} style={{ marginRight: 6, verticalAlign: -4 }} />
                      Get Notified
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="soon-cancel"
                  onClick={() => setShowForm(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
              </motion.form>
            )}
          </AnimatePresence>
          <AnimatePresence>
            {submitted && (
              <motion.div
                className="soon-thank"
                key="thanks"
                initial={{ opacity: 0, scale: 0.93 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ type: "spring", stiffness: 160 }}
                style={{ marginTop: "2.2em" }}
              >
                <div className="soon-thank-msg" style={{ marginTop: "0.7em" }}>
                  Thank you! You’ll be first to know.
                </div>
                <button
                  className="soon-cancel"
                  style={{ marginTop: "1.3em" }}
                  onClick={() => {
                    setSubmitted(false);
                    setShowForm(false);
                    setEmail("");
                  }}
                >
                  Notify another email
                </button>
              </motion.div>
            )}
          </AnimatePresence>
          <motion.div
            className="soon-socials"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.47 }}
          >
            {socials.map((s, i) => (
              <motion.a
                key={s.name}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="soon-social-link"
                whileHover={{ scale: 1.18, rotate: 8 }}
                transition={{ type: "spring", stiffness: 320, damping: 15, delay: i * 0.04 }}
                aria-label={s.name}
                style={{ outline: "none" }}
              >
                {s.icon}
              </motion.a>
            ))}
          </motion.div>
        </motion.div>
        <motion.div
          className="soon-footer"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          style={{ zIndex: 10 }}
        >
          © {new Date().getFullYear()} Elevate Digital Global – All Rights Reserved
        </motion.div>
      </div>
      <style>{`
        .spin {
          animation: soon-spin 1s linear infinite;
        }
        @keyframes soon-spin {
          0% { transform: rotate(0deg);}
          100% { transform: rotate(360deg);}
        }
      `}</style>
    </>
  );
}
